import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { AddressValidationService } from '@core/services/address-validation/address-validation.service';
import { Validation } from '@core/services/address-validation/address-validation.service';
import { FuseTranslationLoaderService } from '@fuse/services/translation-loader.service';
import { Apollo } from 'apollo-angular';
import gql from 'graphql-tag';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { sentry } from 'sentry';
import english from './i18n/en.json';
import spanish from './i18n/es.json';
import portuguese from './i18n/pt.json';

const SEARCH_QUERY = gql`
  query QueryAddressGeocodeGQL ($input: AddressGeocodeInput!) {
    addressGeocode (input: $input) {
      postalCode
      streetName
      city
      number
      state
      complement
      neighborhood
      formattedAddress
      coords {
        latitude
        longitude
      }
    }
  }
`;

@Component({
  selector: 'fc-address-input',
  templateUrl: './fc-address-input.component.html',
  styleUrls: ['./fc-address-input.component.scss']
})
export class FcAddressInputComponent implements OnInit, OnDestroy {

  @Input()
  public control: FormControl;

  @Input()
  public options = [];

  @Input()
  public disabled: boolean;

  @Input()
  public showSearchIcon = false;

  @Input()
  public requiredMark = false;

  @Input()
  public preloadOptions = false;

  @Input()
  public searchOnFocus = false;

  @Input()
  public translateErrorPrefix: string;

  public validation: Validation;
  private readonly removeSubscriptions = new Subject<void>();

  constructor(
    private readonly fuseTranslationLoaderService: FuseTranslationLoaderService,
    private readonly addressValidationService: AddressValidationService,
    private readonly apollo: Apollo
  ) {
    this.fuseTranslationLoaderService.loadJsonTranslations({ english, portuguese, spanish });
    this.search = this.search.bind(this);
    this.displayWith = this.displayWith.bind(this);
  }

  public ngOnInit(): void {
    this.control.valueChanges.pipe(
      takeUntil(this.removeSubscriptions))
      .subscribe((address) => {
        this.validation = this.addressValidationService.validateAddress(address);

        if (this.validation && this.validation.errors && this.validation.errors.length) {
          this.control.setErrors({
            invalid: true
          });

          return;
        }
      });
  }

  public ngOnDestroy(): void {
    this.removeSubscriptions.next();
    this.removeSubscriptions.complete();
  }

  async search(searchTerm: string): Promise<any[]> {
    return this.apollo
      .query({
        query: SEARCH_QUERY,
        variables: {
          input: {
            formattedAddress: searchTerm,
            language: navigator.language || 'pt-BR'
          },
        },
      })
      .toPromise()
      .then((result) => {
        return result.data['addressGeocode'];
      })
      .catch((err) => {
        sentry.captureException(err);
        return [];
      });
  }

  displayWith(value: any): string {
    return value && value.formattedAddress;
  }
}
